/**
 * AdditionalImagesStrip
 * ─────────────────────────────────────────────────────────────────────────
 * Optional "extra angles" gallery that lives BELOW the existing single-
 * image dropzone on every product Create/Edit form (brand, restaurant,
 * retailer/wholesaler, healthcare). Default UX is a collapsed link —
 * "+ Add more angles (optional)" — so vendors who only need one image
 * see no UI churn. Click expands a 5-slot grid where each slot is its
 * own mini file-picker.
 *
 * Why a separate component, not a full uploader rewrite
 * -----------------------------------------------------
 * The single-image dropzone is wired into a lot of moving parts:
 * `MobileProductShootPicker`, the dataURL → Cloudinary upload-on-submit
 * pipeline, validation, the "Replace / Remove" affordances, and a
 * ProgressBar. Replacing it would risk regressing all of that. This
 * component is purely additive — it lives next to the existing dropzone,
 * stores its values in the same dataURL-or-URL form so the form's submit
 * handler can reuse `uploadImageToCloudinary` to materialise newly-picked
 * images, and contributes a single `additionalImages: string[]` field to
 * the API payload.
 *
 * Value shape
 * -----------
 * `value` is an array of strings. Each entry is either:
 *   - an https:// Cloudinary URL (already saved on the server), or
 *   - a `data:image/...` dataURL (just picked locally, not yet uploaded).
 * The form's submit handler walks the array, uploads any dataURLs, and
 * sends back the resolved URL list. This mirrors the cover-image flow.
 *
 * ORDERING
 * --------
 * Array order IS gallery order — the storefront renders these after the
 * cover in exactly the order they arrive. Before, that order was whatever
 * sequence the vendor happened to pick files in, and the only way to change
 * it was to delete everything after the mistake and re-upload. So the tiles
 * are now reorderable, two ways on purpose:
 *
 *   DRAG — the natural gesture, and what anyone tries first. Native HTML5
 *   drag events, no library: the dashboard has no DnD dependency and this
 *   is five thumbnails, not a kanban board.
 *
 *   ◀ ▶ BUTTONS — because HTML5 drag events DO NOT FIRE ON TOUCH. Drag
 *   alone would silently do nothing on a phone or tablet, and this form is
 *   used on both. The buttons are also the keyboard path (they are real
 *   buttons, reachable by Tab), so the feature is not mouse-only.
 *
 * Reordering only ever permutes `value` and calls `onChange` — no upload,
 * no Cloudinary round-trip. A dataURL that has not been uploaded yet moves
 * with everything else and is resolved at submit exactly as before.
 */
import React, { useRef, useState } from "react";
import {
  ImageHoverPreview,
  ImageLightbox,
  useImagePeek,
} from "./ImagePeek";
import { Alert, Button, Form, Spinner } from "react-bootstrap";
import {
  MdAddPhotoAlternate,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdExpandMore,
  MdExpandLess,
} from "react-icons/md";

const DEFAULT_MAX = 5;
const ACCEPT = ".jpg,.jpeg,.png";
/* Mirror the per-image guardrail used by the cover dropzone. The
   shared sharedUpload validates server-side too; this is just a
   client-side fast-fail so vendors don't waste a minute reading a
   12 MB photo into a dataURL only to be rejected at submit. */
const MAX_BYTES = 10 * 1024 * 1024;

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Failed to read file."));
    reader.readAsDataURL(file);
  });

/**
 * Stable per-tile React keys.
 *
 * The old key was `${index}-${src}`, which is fine while the list only
 * grows or shrinks but wrong the moment it can be reordered: moving a tile
 * changes its key, so React tears the <img> down and mounts a new one, and
 * the thumbnail visibly re-fetches mid-drag. Keying on the source alone
 * keeps each image mounted as it moves. The occurrence suffix exists only
 * because nothing stops a vendor picking the same file twice.
 */
const keyFor = (list) => {
  const seen = new Map();
  return list.map((src) => {
    const base = String(src).slice(0, 96);
    const n = seen.get(base) || 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}#${n}`;
  });
};

const AdditionalImagesStrip = ({
  value,
  onChange,
  disabled = false,
  max = DEFAULT_MAX,
  /* Edit forms pass this so the X on a SAVED angle (an https URL) opens the
     product photo delete dialog, which removes it from the product and from
     Cloudinary immediately, with a confirmation that shows the photo. Without
     it, and always for an unsaved dataURL, the X only drops the tile locally
     and the next save reclaims the orphan. */
  onRequestDeleteSaved,
  /* When expanded={true} on first render the strip shows itself even
     if the value array is empty — used by the Edit forms so vendors
     who already added angles see them straight away. */
  expanded: expandedProp,
}) => {
  const safeValue = Array.isArray(value) ? value : [];
  const [open, setOpen] = useState(
    expandedProp != null ? !!expandedProp : safeValue.length > 0
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  /* Index being dragged, and the tile it is currently hovering. Both null
     when no drag is in flight. Kept in state rather than a ref because the
     drop indicator and the ghosted source tile are rendered from them. */
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  /* Hover previews and the lightbox. `suppressed` while a reorder drag is in
     flight: a panel that opens mid-gesture sits on top of the very tiles you
     are dragging between. */
  const peek = useImagePeek({ suppressed: dragIndex !== null });

  const locked = disabled || busy;
  const canReorder = !locked && safeValue.length > 1;

  const handlePick = async (event) => {
    const file = event.target.files?.[0];
    /* Always reset the input so picking the same filename twice in a
       row still triggers onChange. Without this, replacing a removed
       slot with the same source file silently no-ops. */
    if (event.target) event.target.value = "";
    if (!file) return;

    if (file.size > MAX_BYTES) {
      setError("Each image must be 10 MB or smaller.");
      return;
    }
    if (safeValue.length >= max) {
      setError(`Maximum of ${max} additional angles.`);
      return;
    }

    setError("");
    setBusy(true);
    try {
      const dataUrl = await readAsDataURL(file);
      onChange([...safeValue, dataUrl]);
    } catch (err) {
      setError(err?.message || "Failed to read image.");
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = (index) => {
    const src = safeValue[index];
    if (
      typeof onRequestDeleteSaved === "function" &&
      typeof src === "string" &&
      /^https?:\/\//i.test(src)
    ) {
      onRequestDeleteSaved(src);
      return;
    }
    onChange(safeValue.filter((_, i) => i !== index));
  };

  /** Lift one tile out and drop it back in at `to`. The only mutation. */
  const move = (from, to) => {
    const last = safeValue.length - 1;
    if (from === to || from < 0 || to < 0 || from > last || to > last) return;
    const next = safeValue.slice();
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const endDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragStart = (index) => (event) => {
    if (!canReorder) {
      event.preventDefault();
      return;
    }
    setDragIndex(index);
    event.dataTransfer.effectAllowed = "move";
    /* Firefox refuses to start a drag unless some data is set. The payload
       is never read — `dragIndex` is the source of truth, because reading
       dataTransfer during dragover is blocked in most browsers anyway. */
    try {
      event.dataTransfer.setData("text/plain", String(index));
    } catch (_) {
      /* Some embedded webviews throw here. The drag still works. */
    }
  };

  const handleDragOver = (index) => (event) => {
    if (dragIndex == null) return;
    /* Without preventDefault the browser treats this as "not a drop target"
       and the drop event never fires. */
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
    if (index !== overIndex) setOverIndex(index);
  };

  const handleDrop = (index) => (event) => {
    if (dragIndex == null) return;
    event.preventDefault();
    move(dragIndex, index);
    endDrag();
  };

  const remaining = Math.max(0, max - safeValue.length);
  const keys = keyFor(safeValue);

  if (!open) {
    return (
      <div style={styles.collapsedRow}>
        <Button
          type="button"
          variant="link"
          onClick={() => setOpen(true)}
          disabled={disabled}
          style={styles.toggleLink}
        >
          <MdExpandMore size={18} style={{ marginRight: 4 }} />
          Add more angles (optional)
        </Button>
        {safeValue.length > 0 && (
          <span style={styles.count}>
            {safeValue.length} extra
            {safeValue.length === 1 ? "" : " images"}
          </span>
        )}
      </div>
    );
  }

  return (
    <div style={styles.expandedWrap}>
      <div style={styles.headerRow}>
        <span style={styles.headerLabel}>
          Extra angles{" "}
          <span style={styles.headerHint}>
            ({safeValue.length}/{max})
          </span>
        </span>
        <Button
          type="button"
          variant="link"
          onClick={() => setOpen(false)}
          disabled={disabled || busy}
          style={styles.toggleLink}
        >
          <MdExpandLess size={18} style={{ marginRight: 4 }} />
          Hide
        </Button>
      </div>

      <div style={styles.grid}>
        {safeValue.map((src, i) => {
          const isDragging = dragIndex === i;
          const isTarget = dragIndex != null && overIndex === i && !isDragging;
          return (
            <div
              key={keys[i]}
              style={{
                ...styles.tile,
                ...(canReorder ? styles.tileDraggable : null),
                ...(isDragging ? styles.tileDragging : null),
                ...(isTarget ? styles.tileTarget : null),
              }}
              draggable={canReorder}
              onDragStart={handleDragStart(i)}
              onDragOver={handleDragOver(i)}
              onDrop={handleDrop(i)}
              onDragEnd={endDrag}
            >
              {/* A button, not a bare img: hover AND focus open the preview,
                  so the keyboard reaches it, and Enter opens the full view.
                  Deliberately NOT carrying the `draggable={false}` +
                  onDragStart guards that the X and the arrows use — those
                  exist to stop the parent tile dragging, and dragging from the
                  picture is exactly the gesture people expect to work. */}
              <button
                type="button"
                style={styles.tilePeekBtn}
                onClick={() => {
                  // A click arriving as a drag ends is the drag, not a click.
                  if (dragIndex !== null) return;
                  peek.openLightbox(i);
                }}
                aria-label={`View extra angle ${i + 1} larger`}
                title="Click to view larger"
                {...peek.handlers(i)}
              >
                <img
                  src={src}
                  alt={`Extra angle ${i + 1}`}
                  style={styles.tileImg}
                  draggable={false}
                />
              </button>

              <button
                type="button"
                onClick={() => handleRemove(i)}
                disabled={locked}
                style={styles.removeBtn}
                aria-label={`Remove extra angle ${i + 1}`}
                /* Without this the browser starts dragging the tile when a
                   press lands on the X, and the click never arrives. */
                draggable={false}
                onDragStart={(e) => e.preventDefault()}
              >
                <MdClose size={14} />
              </button>

              {/* Position + the touch/keyboard reorder path. Always visible
                  rather than revealed on hover: there is no hover on a
                  phone, and a control you cannot discover is not a
                  fallback. */}
              <div style={styles.orderBar}>
                <button
                  type="button"
                  onClick={() => move(i, i - 1)}
                  disabled={locked || i === 0}
                  style={{
                    ...styles.orderBtn,
                    ...(locked || i === 0 ? styles.orderBtnOff : null),
                  }}
                  aria-label={`Move extra angle ${i + 1} earlier`}
                  title="Move earlier"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <MdChevronLeft size={14} />
                </button>
                <span style={styles.orderNum}>{i + 1}</span>
                <button
                  type="button"
                  onClick={() => move(i, i + 1)}
                  disabled={locked || i === safeValue.length - 1}
                  style={{
                    ...styles.orderBtn,
                    ...(locked || i === safeValue.length - 1
                      ? styles.orderBtnOff
                      : null),
                  }}
                  aria-label={`Move extra angle ${i + 1} later`}
                  title="Move later"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <MdChevronRight size={14} />
                </button>
              </div>
            </div>
          );
        })}

        {remaining > 0 && (
          <label
            style={{
              ...styles.addTile,
              ...(disabled || busy ? styles.addTileDisabled : null),
            }}
          >
            <Form.Control
              ref={inputRef}
              type="file"
              accept={ACCEPT}
              onChange={handlePick}
              disabled={disabled || busy}
              style={{ display: "none" }}
            />
            {busy ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <>
                <MdAddPhotoAlternate size={26} style={{ color: "#0a578bff" }} />
                <span style={styles.addLabel}>Add</span>
              </>
            )}
          </label>
        )}
      </div>

      {/* ONE preview and ONE lightbox for the whole strip, however many tiles
          it has. Both render through a portal at <body>, because the grid
          clips on both axes. */}
      <ImageHoverPreview
        src={peek.hoverIndex !== null ? safeValue[peek.hoverIndex] : null}
        rect={peek.hoverRect}
        alt={`Extra angle ${(peek.hoverIndex ?? 0) + 1}`}
      />

      <ImageLightbox
        images={safeValue}
        index={peek.lightboxIndex}
        onIndex={peek.setLightboxIndex}
        onClose={peek.closeLightbox}
        alt="Extra angle"
      />

      {error && (
        <Alert variant="danger" className="mt-2 mb-0" style={styles.errorAlert}>
          {error}
        </Alert>
      )}

      <div style={styles.footnote}>
        Up to {max} extra angles. The cover above stays as the primary
        thumbnail; these appear after it in the storefront gallery, in the
        order shown here.
        {safeValue.length > 1 ? " Drag a tile, or use ‹ ›, to reorder." : ""}
      </div>
    </div>
  );
};

const styles = {
  collapsedRow: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginTop: 8,
  },
  toggleLink: {
    padding: 0,
    color: "#0a578bff",
    textDecoration: "none",
    fontWeight: 500,
    display: "inline-flex",
    alignItems: "center",
  },
  count: {
    fontSize: 12,
    color: "#6c757d",
  },
  expandedWrap: {
    marginTop: 12,
    padding: "12px 14px",
    borderRadius: 8,
    background: "#f8fafc",
    border: "1px dashed #cbd5e1",
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  headerLabel: {
    fontSize: 13,
    fontWeight: 600,
    color: "#0f172a",
  },
  headerHint: {
    fontWeight: 400,
    color: "#6c757d",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))",
    gap: 10,
    /* Cap the height so a vendor with many angles gets an internal
       scroll rather than a runaway column that stretches the form. */
    maxHeight: 320,
    overflowY: "auto",
    paddingRight: 2,
  },
  tile: {
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 6,
    overflow: "hidden",
    background: "#fff",
    border: "1px solid #e2e8f0",
  },
  /* The picture fills its tile and the button around it is invisible: it is
     there for the hover, the focus ring and the Enter key, not to look like a
     control of its own. */
  tilePeekBtn: {
    display: "block",
    width: "100%",
    height: "100%",
    padding: 0,
    border: 0,
    background: "transparent",
    cursor: "zoom-in",
  },
  tileDraggable: {
    cursor: "grab",
  },
  tileDragging: {
    opacity: 0.4,
  },
  /* The drop target. An outline rather than a border so the tile does not
     shift by a pixel as the highlight appears and disappears. */
  tileTarget: {
    outline: "2px solid #0a578bff",
    outlineOffset: -2,
  },
  tileImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  removeBtn: {
    position: "absolute",
    top: 4,
    right: 4,
    width: 22,
    height: 22,
    borderRadius: "50%",
    border: "none",
    background: "rgba(0,0,0,0.65)",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  },
  orderBar: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    height: 22,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 2px",
    background: "rgba(0,0,0,0.55)",
  },
  orderBtn: {
    width: 20,
    height: 20,
    borderRadius: 4,
    border: "none",
    background: "transparent",
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
  },
  orderBtnOff: {
    opacity: 0.3,
    cursor: "default",
  },
  orderNum: {
    fontSize: 11,
    fontWeight: 700,
    color: "#fff",
    lineHeight: 1,
    fontVariantNumeric: "tabular-nums",
  },
  addTile: {
    aspectRatio: "1 / 1",
    borderRadius: 6,
    border: "1px dashed #94a3b8",
    background: "#fff",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    cursor: "pointer",
  },
  addTileDisabled: {
    cursor: "not-allowed",
    opacity: 0.6,
  },
  addLabel: {
    fontSize: 11,
    color: "#0a578bff",
    fontWeight: 500,
  },
  errorAlert: {
    fontSize: 12,
    padding: "8px 12px",
  },
  footnote: {
    marginTop: 10,
    fontSize: 11,
    color: "#64748b",
  },
};

export default AdditionalImagesStrip;
